import React, { useRef } from 'react';
import { ComparePreset, DateRange, LocalTime } from '../../model/datePicker/types';
import { formatRangeReadout, formatFooterSuffix } from './format';
import './footer.scss';
import { t } from '../../i18n';

const COMPARE_ITEMS: { preset: ComparePreset, label: string }[] = [
  { preset: 'prev-period', label: t('datePicker.comparePresets.prev-period') },
  { preset: 'prev-month', label: t('datePicker.comparePresets.prev-month') },
  { preset: 'prev-quarter', label: t('datePicker.comparePresets.prev-quarter') },
  { preset: 'prev-year', label: t('datePicker.comparePresets.prev-year') },
  { preset: 'custom', label: t('datePicker.comparePresets.custom') }
];

// A todo el ancho, barra lateral incluida, porque gobierna el picker entero
// — §04. Dos filas: chips de comparación (radiogroup), y resumen + acciones.
// El resumen lleva aria-live: cuando B se recalcula por un cambio en A, un
// lector de pantalla lo anuncia solo (§10) — el recálculo nunca es silencioso.
const Footer = ({ comparePreset, onSelectCompare, a, b, days, cutoff, onCancel, onApply }: Props) => {
  const chipsRef = useRef<HTMLDivElement>(null);

  const move = (delta: number) => {
    const currentIndex = COMPARE_ITEMS.findIndex(item => item.preset === comparePreset);
    const fromIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (fromIndex + delta + COMPARE_ITEMS.length) % COMPARE_ITEMS.length;
    const next = COMPARE_ITEMS[nextIndex];
    onSelectCompare(next.preset);
    chipsRef.current?.querySelector<HTMLButtonElement>(`[data-preset="${next.preset}"]`)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if(event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    }
    else if(event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    }
  };

  return (
    <div className="rdp-footer">
      <div className="rdp-footer__row" role="radiogroup" aria-label={t('datePicker.compareTo')} ref={chipsRef} onKeyDown={onKeyDown}>
        <span className="rdp-footer__label">{t('datePicker.compareTo')}</span>
        {
          COMPARE_ITEMS.map(item => {
            const checked = comparePreset === item.preset;
            return (
              <button
                key={item.preset}
                type="button"
                role="radio"
                aria-checked={checked}
                data-preset={item.preset}
                tabIndex={checked ? 0 : -1}
                className={`rdp-footer__chip ${checked ? 'rdp-footer__chip--selected' : ''}`}
                onClick={() => onSelectCompare(item.preset)}
              >
                {item.label}
              </button>
            );
          })
        }
      </div>
      <div className="rdp-footer__row rdp-footer__row--summary">
        <div className="rdp-footer__readout" aria-live="polite">
          <span className="rdp-footer__readout-a">{formatRangeReadout(a)}</span>
          {` ${t('common.vs')} `}
          <span className="rdp-footer__readout-b">{formatRangeReadout(b)}</span>
          {' '}
          <span className="rdp-footer__suffix">{formatFooterSuffix(days, cutoff)}</span>
        </div>
        <div className="rdp-footer__actions">
          <button type="button" className="rdp-footer__cancel" onClick={onCancel}>{t('common.cancel')}</button>
          <button type="button" className="rdp-footer__apply" onClick={onApply}>{t('common.apply')}</button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  comparePreset: ComparePreset,
  onSelectCompare: (preset: ComparePreset) => void,
  a: DateRange,
  b: DateRange,
  days: number,
  cutoff: LocalTime | null,
  onCancel: () => void,
  onApply: () => void
}

export { Footer };
